import { useEffect, useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';
import { ElerhetosegDoboz } from '../reszek/ElerhetosegDoboz.tsx';

import { api, type PartnerkartonValasz, type SzerzodesNezet } from '../api.ts';
import { ft, szam } from '../formazas.ts';
import { letolt } from '../letoltes.ts';
import { szerzodesNyomtatas } from '../reszek/szerzodes-dokumentum.ts';

/**
 * Partnerkarton. (Megbízás 6.5)
 *
 * Nem menüpont: a Napi bontásból és a Törzsadatokból nyílik, és a „vissza"
 * gomb oda visz, ahonnan jöttél.
 */
export function Partnerkarton({
  etterem_id,
  onVissza,
  honnan,
}: {
  etterem_id: number;
  onVissza: () => void;
  honnan: string;
}) {
  /** Letöltés csak engedéllyel. (3.0 / 1.) */
  const letolthet = useMuvelet('export_kimutatas');
  const [karton, setKarton] = useState<PartnerkartonValasz | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  /** Két fül: a forgalom (a korábbi tartalom) és a szerződéstörténet. */
  const [ful, setFul] = useState<'forgalom' | 'szerzodesek'>('forgalom');

  useEffect(() => {
    api
      .get<PartnerkartonValasz>(`/api/kimutatas/karton/${etterem_id}`)
      .then(setKarton)
      .catch((h: Error) => setHiba(h.message));
  }, [etterem_id]);

  if (hiba) return <div className="uzenet bad">{hiba}</div>;
  if (!karton) return <div className="halvany" style={{ padding: 20 }}>Betöltés…</div>;

  const e = karton.etterem;
  const legnagyobbHavi = Math.max(1, ...karton.havi_bontas.map((h) => h.liter));

  return (
    <>
      <div className="dayhead">
        <button className="btn sm" onClick={onVissza}>
          ← vissza {honnan}
        </button>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{e.nev}</span>
        {e.csak_gyujtes && <span className="only">csak gyűjtés</span>}
        <div className="vsw">
          <button className={ful === 'forgalom' ? 'on' : ''} onClick={() => setFul('forgalom')}>
            Forgalom
          </button>
          <button className={ful === 'szerzodesek' ? 'on' : ''} onClick={() => setFul('szerzodesek')}>
            Szerződések
          </button>
        </div>
        <div className="stats">
          <div>
            Összes liter<strong>{szam(karton.osszes_liter, 0)}</strong>
          </div>
          <div>
            Összes érték<strong>{ft(karton.osszes_ertek)}</strong>
          </div>
          <div>
            Utolsó vásárlás<strong>{karton.utolso_vasarlas ?? '—'}</strong>
          </div>
        </div>
      </div>

      {/* --- fejadatok --- */}
      <div className="tdet" style={{ background: '#fff', borderColor: 'var(--rule2)' }}>
        <div className="g4">
          <Adat cimke="Cégcsoport" ertek={e.cegcsoport} />
          <Adat cimke="Cím" ertek={[e.cim, e.helyseg].filter(Boolean).join(', ')} />
          <Adat
            cimke="Ár (bruttó)"
            ertek={e.brutto_ar ? `${ft(e.brutto_ar)} Ft/l` : null}
          />
          <Adat
            cimke="F+ ár"
            ertek={e.fplusz_brutto ? `${ft(e.fplusz_brutto)} Ft/l` : null}
          />
          <Adat cimke="MO ár" ertek={e.mo_ar ? ft(e.mo_ar) : null} />
          <Adat cimke="Raktári ár" ertek={e.r_ar ? ft(e.r_ar) : null} />
          <Adat cimke="Fizetési mód" ertek={e.fizetesi_mod ?? 'Készpénz'} />
          <Adat cimke="Cégnév" ertek={e.cegnev} />
          <Adat cimke="Adószám" ertek={e.adoszam} />
          <Adat cimke="Székhely" ertek={e.szekhely} />
          <Adat cimke="Megjegyzés" ertek={e.megjegyzes} />
        </div>

        {/*
         * Telefonszám és e-mail cím itt is rögzíthető — ne kelljen a
         * Partnerek képernyőre átmenni azért, mert a helyszínen kiderült
         * egy új cím. Akinek nincs rá joga, annak csak látszik.
         * (A megrendelő kérése, 2026-09-28.)
         */}
        <ElerhetosegDoboz etterem_id={etterem_id} />
      </div>

      {ful === 'szerzodesek' ? (
        <KartonSzerzodesek etterem_id={etterem_id} />
      ) : (
      <>
      {/* --- négy szám --- */}
      <div className="cards">
        <div className="card">
          <em>Vásárlások száma</em>
          <b>{karton.vasarlasok}</b>
          <span>lezárt napon</span>
        </div>
        <div className="card">
          <em>Átlagos rendelés</em>
          <b>{karton.atlagos_rendeles === null ? '—' : szam(karton.atlagos_rendeles, 0)}</b>
          <span>liter / alkalom</span>
        </div>
        <div className="card">
          <em>Havi átlag</em>
          <b>{karton.havi_atlag === null ? '—' : szam(karton.havi_atlag, 0)}</b>
          <span>liter / hónap</span>
        </div>
        <div className="card">
          <em>Átlagár</em>
          <b>{karton.atlagar === null ? '—' : szam(karton.atlagar, 2)}</b>
          <span>Ft / liter</span>
        </div>
      </div>

      {/* --- havi bontás sávokkal --- */}
      {karton.havi_bontas.length > 0 && (
        <div className="matrix">
          <table className="tt2">
            <thead>
              <tr>
                <th>Hónap</th>
                <th className="n">Liter</th>
                <th className="n">Érték</th>
                <th style={{ width: '46%' }}></th>
              </tr>
            </thead>
            <tbody>
              {karton.havi_bontas.map((h) => (
                <tr key={h.honap}>
                  <td style={{ padding: '6px 10px', fontWeight: 550 }}>{h.honap}</td>
                  <td className="calc2">{szam(h.liter, 0)}</td>
                  <td className="calc2">{ft(h.ertek)}</td>
                  <td style={{ padding: '6px 10px' }}>
                    <span
                      className="bar"
                      style={{ width: `${(h.liter / legnagyobbHavi) * 100}%` }}
                    ></span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* --- minden tétel időrendben --- */}
      <div className="matrix" style={{ maxHeight: 520, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th>Dátum</th>
              <th className="n">Liter</th>
              <th className="n">F+</th>
              <th className="n">Ár</th>
              <th className="n">Érték</th>
              <th>Fizetés</th>
              <th>Sofőr</th>
              <th className="n">MO</th>
              <th className="n">FO</th>
              <th>Megjegyzés</th>
            </tr>
          </thead>
          <tbody>
            {karton.tetelek.map((t, i) => (
              // A forgalmi előzményből jövő sor halványan, jelöléssel:
              // abban csak liter van, ár és sofőr nincs. (2.0 / 6.3)
              <tr key={`${t.datum}-${i}`} className={t.becsult ? 'becsultsor' : undefined}>
                <td style={{ padding: '6px 10px', fontWeight: 550 }}>
                  {t.datum}
                  {t.becsult && <small className="becsultjel">becsült</small>}
                </td>
                <td className="calc2">{t.liter ? szam(t.liter, 0) : '—'}</td>
                <td className="calc2">{t.fplusz ? 'X' : '—'}</td>
                <td className="calc2">{t.egysegar ? szam(t.egysegar, 0) : '—'}</td>
                <td className="calc2">
                  <b>{t.ertek ? ft(t.ertek) : '—'}</b>
                </td>
                <td style={{ padding: '6px 10px' }}>
                  {t.becsult ? '—' : (t.fizetesi_mod ?? 'KP')}
                </td>
                <td style={{ padding: '6px 10px' }}>{t.sofor_nev ?? '—'}</td>
                <td className="calc2">{t.mo ? szam(t.mo, 0) : '—'}</td>
                <td className="calc2">{t.fo ? szam(t.fo, 0) : '—'}</td>
                <td style={{ padding: '6px 10px', color: 'var(--muted)', fontSize: 11.5 }}>
                  {t.megjegyzes ?? ''}
                </td>
              </tr>
            ))}
            {karton.tetelek.length === 0 && (
              <tr>
                <td colSpan={10} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  Ehhez a partnerhez még nincs lezárt napon rögzített forgalom.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="actbar">
        {letolthet && (
          <button className="btn sm" onClick={() => letolt(`/api/export/karton/${etterem_id}`)}>
            Excel
          </button>
        )}
        <span className="hint">
          Csak a lezárt napok forgalma jelenik meg. Az áfatartalom összesen:{' '}
          {ft(karton.afa)} Ft.
        </span>
      </div>
      </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

const KARTON_ALLAPOT: Record<SzerzodesNezet['allapot'], string> = {
  elo: 'élő',
  lejart: 'lejárt',
  ujra: 'újrakötve',
  lezart: 'lezárva',
  helyszini: 'helyszíni',
  kikuldott: 'kiküldve',
};

/**
 * A partner összes szerződése, típus szerint bontva, a legújabb elöl —
 * az újrakötött és lezárt szerződések halványan. Ez az a nézet, ahol három
 * év múlva is visszakereshető, kinek mikor volt szerződése.
 */
function KartonSzerzodesek({ etterem_id }: { etterem_id: number }) {
  const [lista, setLista] = useState<SzerzodesNezet[] | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ szerzodesek: SzerzodesNezet[] }>(`/api/szerzodes/etterem/${etterem_id}`)
      .then((v) => setLista(v.szerzodesek))
      .catch((h: Error) => setHiba(h.message));
  }, [etterem_id]);

  if (hiba) return <div className="uzenet bad">{hiba}</div>;
  if (!lista) return <div className="halvany" style={{ padding: 20 }}>Betöltés…</div>;
  if (lista.length === 0) {
    return (
      <div className="uzenet halvany">Ennek az étteremnek nincs szerződése a rendszerben.</div>
    );
  }

  const nyomtat = (s: SzerzodesNezet) =>
    void szerzodesNyomtatas(s.id).catch((h: unknown) =>
      setHiba(h instanceof Error ? h.message : 'A dokumentum nem készült el.'),
    );

  return (
    <>
      {(['MO', 'FO'] as const).map((tipus) => {
        const sajat = lista.filter((s) => s.tipus === tipus);
        if (sajat.length === 0) return null;
        const mo = tipus === 'MO';
        return (
          <div key={tipus} style={{ marginTop: 14 }}>
            <p className="lbl">
              {mo ? 'MO — moslék' : 'FO / HSO'} <b>{sajat.length}</b>
            </p>
            <div className="matrix">
              <table className="tt2">
                <thead>
                  <tr>
                    <th>Szerződésszám</th>
                    <th className="n">Kötés</th>
                    {mo && <th className="n">Lejárat</th>}
                    <th>Állapot</th>
                    <th className="n">{mo ? 'Hordó' : 'Edény'}</th>
                    {!mo && <th>Nyil.</th>}
                    <th>Megjegyzés</th>
                    <th style={{ width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {sajat.map((s) => (
                    <tr key={s.id} className={s.allapot === 'ujra' || s.allapot === 'lezart' ? 'gyr' : undefined}>
                      <td className="nm3">
                        <b>{s.szam ?? 'ideiglenes'}</b>
                      </td>
                      <td className="calc2">{s.kotes ?? '—'}</td>
                      {mo && <td className="calc2">{s.lejarat ?? '—'}</td>}
                      <td className={s.allapot === 'lejart' ? 'lejart' : undefined} style={{ padding: '6px 10px' }}>
                        {KARTON_ALLAPOT[s.allapot]}
                        {s.allapot === 'elo' && s.nap !== null && ` · ${s.nap} nap`}
                        {s.allapot === 'lejart' && s.nap !== null && ` · ${-s.nap} napja`}
                      </td>
                      <td className="calc2">{(mo ? s.hordo_db : s.edeny_db) ?? '—'}</td>
                      {!mo && (
                        <td style={{ padding: '6px 10px' }}>
                          {s.nyilatkozat ? 'van' : <span className="only">hiányzik</span>}
                        </td>
                      )}
                      <td style={{ padding: '6px 10px', color: 'var(--muted)', fontSize: 11 }}>
                        {s.megjegyzes ?? ''}
                      </td>
                      <td className="act">
                        <button className="btn sm" onClick={() => nyomtat(s)}>
                          Nyomtat
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </>
  );
}

function Adat({ cimke, ertek }: { cimke: string; ertek: string | null }) {
  return (
    <div>
      <label>{cimke}</label>
      <div style={{ fontSize: 12.5, padding: '5px 0', minHeight: 20 }}>
        {ertek || <span className="halvany">—</span>}
      </div>
    </div>
  );
}
